import { useQuery } from "@tanstack/react-query";

export const USER_QUERY_KEY = ["user"];

interface User {
  id: string;
  // What the backend actually sets on the cached record — a numeric primary
  // key. The `id: string` field above exists on the interface but is not
  // populated anywhere in the app. Prefer `_id` for real user identity.
  _id?: number;
  email?: string;
  name?: string;
  firstName?: string;
  lastName?: string;
  role?: string;
}

interface UseUserReturn {
  user: User | undefined;
  isLoggedIn: boolean;
  isLoading: boolean;
  error: Error | null;
}

/**
 * Hook to access current user data from React Query cache
 * This hook provides access to user data stored by the login mutation
 */
export const useUser = (): UseUserReturn => {
  const { data: user, isLoading, error } = useQuery<User>({
    queryKey: USER_QUERY_KEY,
    queryFn: () => {
      // This query doesn't fetch from server - it only reads from cache
      // The data is set by the login mutation
      throw new Error("User data should be set by login mutation");
    },
    enabled: false, // Never fetch from server, only read from cache
    staleTime: Infinity, // User data doesn't go stale
    gcTime: 1000 * 60 * 60 * 24, // Keep in cache for 24 hours
  });

  return {
    user,
    isLoggedIn: Boolean(user),
    isLoading,
    error,
  };
};